import { useStaticQuery, graphql } from "gatsby"
import { useCore } from "@app/hooks/useCore"
import { useConfigContext } from "@app/providers/config"

export const useMeta = () => {
  const {
    store,
    settings: { routes },
  } = useConfigContext()
  const { helpers } = useCore()
  const { global, languages, organisation, tracking, scripts, social, payments } = useStaticQuery<GatsbyTypes.StaticMetaQuery>(graphql`
    query StaticMeta {
      global: sanityTemplateGlobal {
        title
        description
      }
      languages: sanitySettingLanguages {
        hrefLangs: languages {
          _key
          language
          region
          url
        }
      }
      organisation: sanitySettingOrganisation {
        title
        alternateTitle
        description
        author
        logo {
          asset {
            url
          }
        }
        addressLocality
        addressRegion
        postalCode
        streetAddress
        country
        telephone
        contactType
        email
        areaServed
        availableLanguage
        separator
        og_title
        og_desc
        url
        returnPolicyName
        returnPolicyUrl
        returnDays
        returnMethod
        inStoreReturnsOffered
        offerCatalog {
          title
          url
          descriptionHtml
        }
      }
      social: sanitySettingSocial {
        channels {
          type
          url
        }
      }
      payments: sanitySettingPayments {
        enabled {
          value
        }
        checkoutLabel
      }
      tracking: sanitySettingTracking {
        facebookAppId
        googleSiteVerify
        googleTagManagerId
      }
      scripts: sanitySettingScripts {
        trackingHeader {
          code
        }
        trackingBody {
          code
        }
        trackingFooter {
          code
        }
      }
    }
  `)

  const mapping = {
    page: helpers.schemaWebSite,
    blog: helpers.schemaBlog,
    article: helpers.schemaArticle,
    product: helpers.schemaProduct,
    collection: helpers.schemaWebSite,
    search: helpers.schemaSearch,
    store: helpers.schemaStore,
  }

  const getData = ({
    breadcrumbs,
    data,
    language,
    routes,
    template,
    url,
  }: {
    breadcrumbs: any
    data: any
    language: any
    routes: any
    template: any
    url: any
  }) => {
    // schemaData reads listItems before it is initialised when given breadcrumbs, so the trail is inserted here instead
    const metadata = helpers.schemaData(
      { breadcrumbs: [], data, global, language, organisation, routes, social, template, tracking, url },
      mapping
    )

    if (!breadcrumbs?.length) return metadata

    const [homepage, page] = metadata.listItems

    return {
      ...metadata,
      listItems: [
        homepage,
        ...breadcrumbs.map(({ name, url }: any, index: number) =>
          helpers.schemaListItem({ name, url: `${metadata.siteURL}${url}`, position: index + 2 })
        ),
        { ...page, position: breadcrumbs.length + 2 },
      ],
    }
  }

  const getTags = (data: any) =>
    helpers
      .metaTags(data)
      .map(tag =>
        tag.property === "og:title" && organisation?.og_title
          ? { ...tag, content: organisation?.og_title }
          : tag.property === "og:description" && organisation?.og_desc
            ? { ...tag, content: organisation?.og_desc }
            : tag
      )

  const getLanguages = (route: any) =>
    languages?.hrefLangs
      ?.filter((item): item is NonNullable<typeof item> => item != null)
      ?.map(({ _key, language, region, url }, index: number) => ({
        key: _key || "",
        primary: !index,
        href: `${url || organisation?.url}${route || ""}`,
        hrefLang: `${region ? `${language}-${region}` : `${language}`}`,
        language: `${region ? `${language}-${region}` : `${language}`}`,
        rel: "alternate",
      }))

  const storeHelper = (data: any) => {
    if (data?.type !== "store") return null

    return {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: data?.document?.title || organisation?.title,
      address: {
        "@type": "PostalAddress",
        streetAddress: data?.document?.addressStreet,
        addressLocality: data?.document?.addressCity,
        addressRegion: data?.document?.addressState,
        postalCode: data?.document?.addressPostCode,
        addressCountry: data?.document?.addressCountry,
      },
      telephone: data?.document?.phone,
      url: organisation?.url,
    }
  }

  const organisationHelper = (data: any) => {
    // Use helpers to generate the initial Organisation Schema Markup
    if (!data?.document?.homepage) return null
    const organisationSchemaObject: any = helpers.schemaOrg(data)
    const hasMerchantReturnPolicy = {
      "@type": "MerchantReturnPolicy",
      name: organisation?.returnPolicyName,
      url: organisation?.returnPolicyUrl,
      merchantReturnDays: organisation?.returnDays,
      returnMethod: organisation?.returnMethod,
      inStoreReturnsOffered: organisation?.inStoreReturnsOffered,
    }

    const contactPoint = {
      "@type": "ContactPoint",
      telephone: organisation?.telephone,
      contactType: organisation?.contactType,
      email: organisation?.email,
      areaServed: organisation?.areaServed,
      availableLanguage: organisation?.availableLanguage,
    }

    organisationSchemaObject.hasMerchantReturnPolicy = hasMerchantReturnPolicy
    organisationSchemaObject.contactPoint = contactPoint
    organisationSchemaObject.description = organisation?.description?.replace(/<[^>]+>/g, "")
    if (organisationSchemaObject.address) {
      organisationSchemaObject.address.addressCountry = organisation?.country
    }
    if (payments) {
      const paymentAccepted = payments.enabled?.map((item: any) => item.value)
      organisationSchemaObject.acceptedPaymentMethod = paymentAccepted
    }
    if (organisation?.offerCatalog?.length) {
      const hasOfferCatalog = {
        "@type": "OfferCatalog",
        "@id": organisation.url,
        name: organisation.title,
        url: organisation.url,
        numberOfItems: organisation.offerCatalog.length,
        itemListElement: organisation.offerCatalog.map((item: any) => ({
          "@type": "OfferCatalog",
          name: item.title,
          description: item.descriptionHtml.replace(/<[^>]+>/g, ""),
          url: item.url,
        })),
      }
      organisationSchemaObject.hasOfferCatalog = hasOfferCatalog
    }
    return organisationSchemaObject
  }

  const faqHelper = (data: any) => {
    if (data?.document?.handle?.current !== "contact-us") return null

    const accordionGroup = data?.document?.content?.find((item: any) => item._type === "sectionAccordionGroup")

    if (!accordionGroup || !accordionGroup.generateSchemaMarkup) return null

    const entityObject =
      accordionGroup.accordions?.flatMap((group: any) =>
        group.accordionItems?.map((item: any) => ({
          "@type": "Question",
          name: item?.question || "",
          acceptedAnswer: {
            "@type": "Answer",
            text: item?.answer?.map((block: any) => block?.children?.map((child: any) => child?.text).join(" ")).join(" ") || "",
          },
        }))
      ) || []

    return entityObject.length
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: entityObject,
        }
      : null
  }

  const getProductSchema = (data: any) => {
    if (!data?.document) return null

    const doc = data.document

    const productSchema: any = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: data.document.title,
      image: doc.featuredImage?.originalSrc ? [doc.featuredImage.originalSrc] : [],
      sku: doc?.variants?.[0]?.sku,
      gtin: doc?.variants?.[0]?.barcode || undefined,
      description: doc.description,
      url: `https://${store.url}${doc.url}/${doc.handle}`,
      brand: {
        "@type": "Brand",
        name: doc.vendor, // fallback brand
      },
      offers: {
        "@type": "Offer",
        url: `https://${store.url}${doc.url}/${doc.handle}`,
        priceCurrency: doc.currency || "AUD",
        price: doc.price || doc.priceRange?.minVariantPrice?.amount || "0.00",
        itemCondition: "https://schema.org/NewCondition",
        availability: "https://schema.org/InStock",
      },
    }

    // Add AggregateRating and Reviews if available
    if (doc.reviews?.averageScore && doc.reviews?.reviews?.length > 0) {
      productSchema.aggregateRating = {
        "@type": "AggregateRating",
        ratingValue: doc.reviews.averageScore,
        reviewCount: doc.reviews.totalReviews || doc.reviews.reviews.length,
      }

      productSchema.review = doc.reviews.reviews.map((review: any) => ({
        "@type": "Review",
        author: {
          "@type": "Person",
          name: review.author || "Anonymous",
        },
        datePublished: review.createdAt?.split("T")[0], // ISO date (no time)
        reviewBody: review.content,
        name: review.title,
        reviewRating: {
          "@type": "Rating",
          ratingValue: review.score,
          bestRating: 5,
          worstRating: 1,
        },
      }))
    }

    return productSchema
  }

  const getSchemas = (data: any) => {
    return [
      data?.type === "product" ? getProductSchema(data) : helpers.schemaContent(data),
      helpers.schemaBreadcrumbs(data),
      storeHelper(data),
      faqHelper(data),
      organisationHelper(data),
    ]
      ?.filter(schema => schema)
      ?.filter(schema => schema["@type"] !== "Organization" || data?.pageUrl === `${data.siteURL}${routes.HOMEPAGE}`)
  }
  const getTracking = () =>
    helpers?.isDomReady
      ? [
          helpers.renderTrackingScripts(scripts?.trackingHeader?.code || "", { id: "tracking-head", placement: "head" }),
          helpers.renderTrackingScripts(scripts?.trackingBody?.code || "", { id: "tracking-body", placement: "body" }),
          helpers.renderTrackingScripts(scripts?.trackingFooter?.code || "", { id: "tracking-foot", placement: "foot" }),
        ]
      : []

  return {
    getData,
    getLanguages,
    getSchemas,
    getTags,
    getTracking,
  }
}
